<script lang="ts">
	// Bulk actions over the sidebar's selected chats (SAY-1820). Appears at the
	// bottom of the sidebar while selection mode is on. What it offers follows
	// what is selected: live chats can be moved to a folder or to the Trash;
	// trashed chats can be restored or deleted for good (behind a confirm).
	import FolderInputIcon from '@lucide/svelte/icons/folder-input';
	import FolderXIcon from '@lucide/svelte/icons/folder-x';
	import Trash2Icon from '@lucide/svelte/icons/trash-2';
	import RotateCcwIcon from '@lucide/svelte/icons/rotate-ccw';
	import ChevronDownIcon from '@lucide/svelte/icons/chevron-down';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js';
	import ConfirmDeleteDialog from './ConfirmDeleteDialog.svelte';
	import Spinner from './Spinner.svelte';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { getSessions, markTrashed, markRestored, removeSession } from '$lib/sessions-store.svelte';
	import { getFolders, moveToFolder, forgetSession } from '$lib/folders-store.svelte';
	import { forgetTab } from '$lib/chat-tabs.svelte';
	import { clearSelection, isSelectionMode, selectedIds, setSelectionMode } from '$lib/chat-selection.svelte';
	import { trashSession, restoreSession, deleteSession } from '$lib/api';
	import { toast } from 'svelte-sonner';

	const sessions = getSessions();
	const folders = getFolders();
	const selected = selectedIds();

	const picked = $derived(sessions.filter((s) => selected.has(s.sessionId)));
	const live = $derived(picked.filter((s) => !s.deletedAt));
	const trashed = $derived(picked.filter((s) => s.deletedAt));

	let busy = $state<'move' | 'trash' | 'restore' | 'delete' | null>(null);
	let confirmOpen = $state(false);

	// Run one action per chat, keep going on failures, report the tally.
	async function each(ids: string[], fn: (id: string) => Promise<void>): Promise<{ ok: number; failed: number }> {
		let ok = 0;
		let failed = 0;
		for (const id of ids) {
			try {
				await fn(id);
				ok += 1;
			} catch (err) {
				failed += 1;
				console.warn('[bulk] action failed for', id, err);
			}
		}
		return { ok, failed };
	}

	function finish(label: string, r: { ok: number; failed: number }): void {
		if (r.failed === 0) toast.success(`${label} ${r.ok} ${r.ok === 1 ? 'chat' : 'chats'}`);
		else toast.error(`${label} ${r.ok}, failed for ${r.failed}`);
		clearSelection();
	}

	async function move(folderId: string | null): Promise<void> {
		if (busy || live.length === 0) return;
		busy = 'move';
		try {
			await moveToFolder(live.map((s) => s.sessionId), folderId);
			const f = folders.find((x) => x.id === folderId);
			toast.success(folderId ? `Moved ${live.length} to ${f?.name ?? 'folder'}` : `Removed ${live.length} from folders`);
			clearSelection();
		} catch (err) {
			toast.error('Could not move the chats', { description: err instanceof Error ? err.message : String(err) });
		} finally {
			busy = null;
		}
	}

	async function trashAll(): Promise<void> {
		if (busy || live.length === 0) return;
		busy = 'trash';
		const ids = live.map((s) => s.sessionId);
		if (ids.includes(page.params.sessionId ?? '')) void goto('/');
		const r = await each(ids, async (id) => {
			markTrashed(id);
			forgetTab(id);
			try {
				await trashSession(id);
			} catch (err) {
				markRestored(id);
				throw err;
			}
		});
		busy = null;
		finish('Moved to Trash', r);
	}

	async function restoreAll(): Promise<void> {
		if (busy || trashed.length === 0) return;
		busy = 'restore';
		const r = await each(trashed.map((s) => s.sessionId), async (id) => {
			markRestored(id);
			try {
				await restoreSession(id);
			} catch (err) {
				markTrashed(id);
				throw err;
			}
		});
		busy = null;
		finish('Restored', r);
	}

	async function deleteAll(): Promise<void> {
		if (busy || trashed.length === 0) return;
		busy = 'delete';
		const r = await each(trashed.map((s) => s.sessionId), async (id) => {
			await deleteSession(id);
			removeSession(id);
			forgetSession(id);
			forgetTab(id);
		});
		busy = null;
		confirmOpen = false;
		finish('Deleted', r);
	}
</script>

{#if isSelectionMode()}
	<div
		class="mx-1 mb-1 rounded-xl border border-gray-200 bg-white p-2 text-xs shadow-sm dark:border-gray-800 dark:bg-gray-900"
		data-testid="bulk-bar"
		role="region"
		aria-label="Bulk actions"
	>
		<div class="mb-1.5 flex items-center justify-between px-0.5">
			<span class="text-gray-600 dark:text-gray-300">
				{#if busy}<Spinner class="mr-1 inline size-3" />{/if}
				{picked.length} selected
			</span>
			<button
				type="button"
				class="text-gray-500 hover:text-gray-900 dark:hover:text-white"
				onclick={() => setSelectionMode(false)}
			>
				Done
			</button>
		</div>
		<div class="flex flex-wrap gap-1">
			{#if live.length > 0}
				<DropdownMenu.Root>
					<DropdownMenu.Trigger
						disabled={busy !== null}
						class="flex h-6 items-center gap-1 rounded-lg bg-gray-100 px-2 text-gray-800 transition hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-850 dark:text-gray-100 dark:hover:bg-gray-800"
						data-testid="bulk-move"
					>
						<FolderInputIcon class="size-3.5" strokeWidth={1.5} aria-hidden="true" />
						Move to
						<ChevronDownIcon class="size-3 opacity-70" aria-hidden="true" />
					</DropdownMenu.Trigger>
					<DropdownMenu.Content align="start" side="top" sideOffset={6} class="min-w-[12rem]">
						{#if folders.length === 0}
							<DropdownMenu.Item disabled>No folders yet</DropdownMenu.Item>
						{/if}
						{#each folders as f (f.id)}
							<DropdownMenu.Item onSelect={() => void move(f.id)}>
								<FolderInputIcon strokeWidth={1.5} aria-hidden="true" />
								<span class="truncate">{f.name}</span>
							</DropdownMenu.Item>
						{/each}
						<DropdownMenu.Separator />
						<DropdownMenu.Item onSelect={() => void move(null)}>
							<FolderXIcon strokeWidth={1.5} aria-hidden="true" />
							No folder
						</DropdownMenu.Item>
					</DropdownMenu.Content>
				</DropdownMenu.Root>
				<button
					type="button"
					disabled={busy !== null}
					class="flex h-6 items-center gap-1 rounded-lg bg-gray-100 px-2 text-gray-800 transition hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-850 dark:text-gray-100 dark:hover:bg-gray-800"
					onclick={() => void trashAll()}
					data-testid="bulk-trash"
				>
					<Trash2Icon class="size-3.5" strokeWidth={1.5} aria-hidden="true" />
					Trash
				</button>
			{/if}
			{#if trashed.length > 0}
				<button
					type="button"
					disabled={busy !== null}
					class="flex h-6 items-center gap-1 rounded-lg bg-gray-100 px-2 text-gray-800 transition hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-850 dark:text-gray-100 dark:hover:bg-gray-800"
					onclick={() => void restoreAll()}
					data-testid="bulk-restore"
				>
					<RotateCcwIcon class="size-3.5" strokeWidth={1.5} aria-hidden="true" />
					Restore
				</button>
				<button
					type="button"
					disabled={busy !== null}
					class="flex h-6 items-center gap-1 rounded-lg bg-red-50 px-2 text-red-700 transition hover:bg-red-100 disabled:opacity-50 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/70"
					onclick={() => (confirmOpen = true)}
					data-testid="bulk-delete"
				>
					<Trash2Icon class="size-3.5" strokeWidth={1.5} aria-hidden="true" />
					Delete forever
				</button>
			{/if}
			{#if picked.length === 0}
				<span class="px-0.5 text-gray-400">Tick chats to act on them</span>
			{/if}
		</div>
	</div>
{/if}

<ConfirmDeleteDialog
	bind:open={confirmOpen}
	title="Delete {trashed.length} {trashed.length === 1 ? 'chat' : 'chats'}?"
	busy={busy === 'delete'}
	onConfirm={() => void deleteAll()}
>
	This will permanently delete the selected {trashed.length === 1 ? 'chat' : 'chats'} with their history and any files they
	created. This cannot be undone.
</ConfirmDeleteDialog>
